import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBath,
  faBed,
  faDollarSign,
  faLocationDot,
  faMagnifyingGlass,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import SEO, { pageSEO } from "../components/seo.component";
import constants from "../constants/constants";
import ListingCard from "../components/listingCard.component";
import Pagination from "../components/pagination.component";
import BouncingDotsLoader from "./loader.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { formatStr } from "../functions/listings";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";
import imgs from "./temp.json";

export default function Listings() {
  const { state } = useLocation();
  const seoConfig = pageSEO.listings();
  const maxListings = 15;
  const [listings, setListings] = useState([]);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("All");
  const [error, setError] = useState("");
  const listingsRef = useRef(null);

  // dummy listings for build files
  const demoListings = data.value.filter(
    listing =>
      (listing.PropertyType || "").toLowerCase().includes("residential")
  );

  const initialSearch = {
    bedrooms: "",
    bathrooms: "",
    minPrice: "",
    maxPrice: "",
    cityArea: "",
    address: ""
  };

  const [searchParams, setSearchParams] = useState(() => {
    const saved = sessionStorage.getItem("listingFilters");

    return saved
      ? JSON.parse(saved)
      : initialSearch;
  });

  const [activeSearch, setActiveSearch] = useState(() => {
    const saved = sessionStorage.getItem("activeListingFilters");

    return saved
      ? JSON.parse(saved)
      : initialSearch;
  });

  const handlePageChange = (newPage) => {
    setPage(newPage);

    if (listingsRef.current) {
      listingsRef.current.scrollIntoView({
        block: "start",
      });
    }
  };

  /* API REQUEST*/
  async function fetchData(url) {
    const body = {
      url,
      needAuth: true,
      bearerToken: constants.TOKEN
    };
    const response =
      constants.ENVIRONMENT === "production"
        ? await fetch(
          `https://${constants.DOMAIN}/api/${url}`
        )
        :
        await fetch(
          `http://${constants.URL}/metaGet`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
          }
        );
    if (!response.ok)
      throw Error("API ERROR");
    return response.json();
  }

  /* FORMAT IMAGES */
  function formatImages(media) {
    return media.reduce((acc, item) => {
      if (!acc[item.ResourceRecordKey])
        acc[item.ResourceRecordKey] = [];
      acc[item.ResourceRecordKey].push(item);
      return acc;
    }, {});
  }


  /* LOAD MEDIA */
  async function loadImages(data) {
    if (!data.length) return;

    const ids = data
      .map(item => `'${item.ListingKey}'`)
      .join(",");

    let url;

    if (constants.ENVIRONMENT === "production") {
      url =
        `odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(${ids})(S)and(S)ImageSizeDescription(S)eq(S)'Thumbnail'(S)and(S)PreferredPhotoYN(S)eq(S)true`;
    } else {
      url =
        `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (${ids}) and ImageSizeDescription eq 'Thumbnail' and PreferredPhotoYN eq true`;
    }

    const result = await fetchData(url);

    setImages(formatImages(result.value));
  }

  /* LOAD LISTINGS */
  async function loadListings() {
    try {
      setLoading(true);

      let filterQuery =
        "StandardStatus eq 'Active'";

      if (filter === "Agent") {
        filterQuery +=
          ` and ListAgentKey eq '${constants.AGENT_ID}'`;
      }

      if (filter === "Office") {
        filterQuery +=
          ` and ListOfficeKey eq '${constants.OFFICE_ID}'`;
      }

      Object.values(searchParams)
        .filter(Boolean)
        .forEach(item => {
          filterQuery +=
            ` and ${item}`;
        });

      // Format only for production
      const finalFilter =
        constants.ENVIRONMENT === "production"
          ? formatStr(filterQuery)
          : filterQuery;

      const url = constants.ENVIRONMENT === "production" ?
        `odata/Property(Q)$filter=${finalFilter}(A)$orderby=OriginalEntryTimestamp(S)desc(A)$top=500`
        :
        `https://query.ampre.ca/odata/Property?$filter=${finalFilter}&$orderby=OriginalEntryTimestamp desc&$top=500`;

      const data =
        await fetchData(url);

      console.log("data", data);

      if (data.value) {
        setListings(data.value);

        await loadImages(
          data.value.slice(
            0,
            maxListings
          )
        );
        setError("");
      }
    }
    catch (error) {
      console.log(error);
      setListings([]);
      setImages({});

      // setError(
      //   "Unable to load listings."
      // );
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    sessionStorage.setItem(
      "listingFilters",
      JSON.stringify(searchParams)
    );
  }, [searchParams]);


  useEffect(() => {
    sessionStorage.setItem(
      "activeListingFilters",
      JSON.stringify(activeSearch)
    );
  }, [activeSearch]);

  useEffect(() => {
    console.log("FILTER", filter)
    loadListings()
  }, [filter, activeSearch]);

  useEffect(() => {
    async function updatePageImages() {
      const data =
        listings.slice(
          (page - 1) * maxListings,
          page * maxListings
        );

      await loadImages(data);
    }
    if (listings.length) {
      updatePageImages();
    }
  }, [page]);

  /* SEARCH UPDATE */
  function updateSearch(key, value) {
    setSearchParams(prev => ({
      ...prev,
      [key]: value
    }));
  }

  function handleSearch() {
    setPage(1);
    setActiveSearch(searchParams);
  }

  function clearSearch() {
    setSearchParams(initialSearch);
    setActiveSearch(initialSearch);
    setFilter("All");
    setPage(1);
  }

  /* PAGINATION */
  // const totalPages =
  //   Math.ceil(
  //     listings.length / maxListings
  //   );

  // const visibleListings =
  //   listings.slice(
  //     (page - 1) * maxListings,
  //     page * maxListings
  //   );

  const totalPages =
    Math.ceil(
      demoListings.length / maxListings
    );

  const visibleListings =
    demoListings.slice(
      (page - 1) * maxListings,
      page * maxListings
    );

  console.log("LSITINGS", listings)
  console.log("VISIBLE", visibleListings)

  return (
    <>
      <SEO {...seoConfig} />
      <section className="max-w-[1600px] mx-auto px-6 pt-40 pb-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Heading */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 border-b border-slate-200 pb-10"
          >
            <div>
              <p
                className="uppercase tracking-[0.5em] font-semibold text-cottage-accent"
              >
                Property Search
              </p>

              <h1 className="mt-6 text-5xl lg:text-6xl font-light font-serif tracking-tight text-harbour-navy leading-none">
                Find Your
                <br />
                Dream Home
              </h1>
            </div>
          </motion.div>

          {/* Search Row */}
          <motion.div
            variants={fadeUp}
            className=" mt-12 rounded-lg border border-slate-200 bg-white /95 backdrop-blur-xl p-8 lg:p-10 shadow-xl"
          >

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5">
              {/* Bedrooms */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faBed}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-cottage-accent"
                />

                <select
                  value={searchParams.bedrooms}
                  onChange={(e) => updateSearch("bedrooms", e.target.value)}
                  className="h-14 w-full appearance-none rounded-2xl border border-slate-200 pl-11 pr-10 text-slate-700 transition-all duration-300 hover:border-cottage-accent focus:border-cottage-accent focus:ring-cottage-accent /10 outline-none"
                >
                  <option value="">Bedrooms</option>
                  <option value="BedroomsTotal eq 1">1 Bedroom</option>
                  <option value="BedroomsTotal eq 2">2 Bedrooms</option>
                  <option value="BedroomsTotal eq 3">3 Bedrooms</option>
                  <option value="BedroomsTotal ge 3">3+ Bedrooms</option>
                </select>
              </div>

              {/* Bathrooms */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faBath}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-cottage-accent"
                />

                <select
                  value={searchParams.bathrooms}
                  onChange={(e) => updateSearch("bathrooms", e.target.value)}
                  className="h-14 w-full appearance-none rounded-2xl border border-slate-200 pl-11 pr-10 text-slate-700 transition-all duration-300 hover:border-cottage-accent focus:border-cottage-accent focus:ring-cottage-accent outline-none"
                >
                  <option value="">Bathrooms</option>
                  <option value="BathroomsTotalInteger eq 1">1 Bathroom</option>
                  <option value="BathroomsTotalInteger eq 2">2 Bathrooms</option>
                  <option value="BathroomsTotalInteger eq 3">3 Bathrooms</option>
                  <option value="BathroomsTotalInteger ge 3">3+ Bathrooms</option>
                </select>
              </div>

              {/* City */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-cottage-accent"
                />

                <input
                  value={
                    searchParams.cityArea
                      ? searchParams.cityArea.match(/'([^']+)'/)?.[1] || ""
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value.trim();

                    if (value) {
                      const city = value.charAt(0).toUpperCase() + value.slice(1);

                      updateSearch(
                        "cityArea",
                        `(contains(City,'${city}') or contains(CityRegion,'${city}') or contains(UnparsedAddress,'${city}'))`
                      );
                    } else {
                      updateSearch("cityArea", "");
                    }
                  }}
                  placeholder="City or Area"
                  className="h-14 w-full appearance-none rounded-2xl border border-slate-200 pl-11 pr-10 text-slate-700 transition-all duration-300 hover:border-cottage-accent focus:border-cottage-accent focus:ring-cottage-accent outline-none"
                />
              </div>

              {/* Min */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-cottage-accent"
                />

                <input
                  value={searchParams.minPrice.replace(/\D/g, "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");

                    updateSearch("minPrice", value ? `ListPrice ge ${value}` : "");
                  }}
                  placeholder="Min Price"
                  className="h-14 w-full appearance-none rounded-2xl border border-slate-200 pl-11 pr-10 text-slate-700 transition-all duration-300 hover:border-cottage-accent focus:border-cottage-accent focus:ring-cottage-accent outline-none"
                />
              </div>

              {/* Max */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-cottage-accent"
                />

                <input
                  value={searchParams.maxPrice.replace(/\D/g, "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");

                    updateSearch("maxPrice", value ? `ListPrice le ${value}` : "");
                  }}
                  placeholder="Max Price"
                  className="h-14 w-full appearance-none rounded-2xl border border-slate-200 pl-11 pr-10 text-slate-700 transition-all duration-300 hover:border-cottage-accent focus:border-cottage-accent focus:ring-cottage-accent outline-none"
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-8 flex flex-wrap justify-end gap-4">
              <button
                onClick={clearSearch}
                className="btn-cottage"
              >
                Clear
              </button>

              <button
                onClick={handleSearch}
                className="btn-cottage-outline-light"
              >
                Search Properties
              </button>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          ref={listingsRef}
        >
          <motion.div
            variants={fadeUp}
            className="mt-24 flex flex-col gap-6 border-b border-slate-200 pb-8 sm:flex-row sm:items-end sm:justify-between mx-5 lg:mx-24">
            <div>
              <p className="mb-3 uppercase tracking-[0.4em] text-cottage-accent">
                Property Collection
              </p>

              <h1 className="text-4xl font-light uppercase tracking-wider text-cottage-primary font-serif">
                Recent Listings
              </h1>
            </div>

            <div className="w-full sm:w-52">
              <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-slate-500">
                Show Listings
              </label>

              <div className="relative">
                <select
                  className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm uppercase tracking-wider text-slate-700 shadow-sm outline-none transition focus:border-cottage-accent"
                  value={filter}
                  onChange={(e) => {
                    setPage(1);
                    setFilter(e.target.value);
                    // setSearching(4);
                    // runSearch(e.target.value);
                  }}
                >
                  <option value="All">
                    All Listings
                  </option>

                  <option value="Office">
                    Office Listings
                  </option>

                  <option value="Agent">
                    Agent Listings
                  </option>
                </select>

                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-harbour-navy">
                  ▼
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
        {loading && (
          <div className="text-center py-20">
            <div className="mt-32 min-h-[20vh]">
              <BouncingDotsLoader />
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-20">

            <div className="inline-block rounded-xl border border-red-200 bg-red-50 px-6 py-4 text-red-700">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mr-2"
              />
              {error}
            </div>
          </div>
        )}

        {!loading && !error && visibleListings.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 py-10 px-4 lg:px-24">
            {visibleListings.map((property) => (
              <ListingCard
                key={property.ListingKey}
                property={property}
                // image={images[property.ListingKey]?.[0]}
                image={property.MainImgUrl} //dummy images
                type="residential"
              />
            ))}
          </div>
        )}

        {!loading && !error && visibleListings.length === 0 && (
          <div className="text-center py-20 tracking-wider text-xl">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="mr-2 text-harbour-navy"
            />
            No properties to show
          </div>
        )}

        <Pagination
          page={page}
          setPage={handlePageChange}
          totalPages={totalPages}
          totalItems={listings.length}
          pageSize={maxListings}
        />
      </section >
    </>
  );
}